import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SectionHeader from "./ui/SectionHeader";
import { cn, spacing, animations } from "../utils/cn";

const InteractiveSkills = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredSkill, setHoveredSkill] = useState(null);

  // Technologies organized by junior front-end level
  const skills = {
    core: [
      { name: 'HTML5', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg', category: 'Solid Foundation' },
      { name: 'CSS3', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg', category: 'Solid Foundation' },
      { name: 'JavaScript ES6+', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg', category: 'Core Language' },
      { name: 'Responsive Design', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg', category: 'Mobile-First' }
    ],
    frontend: [
      { name: 'React', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg', category: 'Components & Hooks' },
      { name: 'CSS Grid', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg', category: 'Layout' },
      { name: 'Flexbox', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg', category: 'Layout' },
      { name: 'Bootstrap', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg', category: 'CSS Framework' }
    ],
    frameworks: [
      { name: 'Tailwind CSS', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-plain.svg', category: 'Utility Framework' },
      { name: 'Material UI', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/materialui/materialui-original.svg', category: 'Component Library' },
      { name: 'Sass/SCSS', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sass/sass-original.svg', category: 'CSS Preprocessor' },
      { name: 'Git', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg', category: 'Version Control' }
    ],
    tools: [
      { name: 'npm', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/npm/npm-original-wordmark.svg', category: 'Package Manager' },
      { name: 'Vite', icon: 'https://vitejs.dev/logo.svg', category: 'Build Tool' },
      { name: 'GitHub', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg', category: 'Code Hosting' },
      { name: 'VS Code', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg', category: 'Code Editor' }
    ],
    deployment: [
      { name: 'Vercel', icon: 'https://assets.vercel.com/image/upload/front/favicon/vercel/57x57.png', category: 'Hosting' },
      { name: 'Netlify', icon: 'https://www.netlify.com/v3/img/components/netlify-color-bg.svg', category: 'Static Hosting' },
      { name: 'Render', icon: 'https://render.com/favicon.ico', category: 'Cloud Platform' },
      { name: 'GitHub Pages', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg', category: 'Static Hosting' }
    ],
    learning: [
      { name: 'Redux', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg', category: 'State Management' },
      { name: 'TypeScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg', category: 'Type Safety' },
      { name: 'Next.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg', category: 'React Framework' },
      { name: 'Node.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg', category: 'Backend Basics' },
      { name: 'Express.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg', category: 'Backend Framework' },
      { name: 'MongoDB', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg', category: 'Database' },
      { name: 'Jest', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg', category: 'Testing' }
    ]
  };

  const allSkills = Object.values(skills).flat();
  const displaySkills = selectedCategory === 'all' ? allSkills : skills[selectedCategory] || [];

  return (
    <section id="skills" className={cn("bg-navy", spacing.section)} data-section="skills">
      <div className={spacing.containerWide}>
        <motion.div {...animations.fadeInUp}>
          <SectionHeader number="02" title={t('skills.title')} />
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {['all', 'core', 'frontend', 'frameworks', 'tools', 'deployment', 'learning'].map((category) => (
              <motion.button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  "px-6 py-3 rounded-full font-mono text-sm transition-all duration-300 relative overflow-hidden",
                  selectedCategory === category 
                    ? "bg-gradient-to-r from-accent to-secondary text-white shadow-lg" 
                    : "bg-lightNavy/50 text-slate hover:text-accent border border-accent/20 hover:border-accent/60"
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {selectedCategory === category && (
                  <motion.div
                    layoutId="activeCategory"
                    className="absolute inset-0 bg-gradient-to-r from-accent to-secondary rounded-full"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">
                  {t(`skills.categories.${category}`)}
                </span>
              </motion.button>
            ))}
          </div>

          {/* VS Code Style Container */}
          <div className="mb-16 rounded-lg overflow-hidden border border-accent/20 bg-gradient-to-br from-lightNavy/90 to-lightNavy/60 shadow-2xl">
            {/* VS Code Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-lightNavy border-b border-accent/20">
              {/* Window Controls */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
              </div>
              
              {/* Tab */}
              <div className="flex items-center space-x-2">
                <div className="px-4 py-1 bg-navy/50 rounded-t border-t border-l border-r border-accent/20 text-accent font-mono text-sm">
                  skills.{selectedCategory === 'all' ? 'json' : `${selectedCategory}.js`}
                </div>
              </div>
              
              {/* Right Controls */}
              <div className="flex items-center space-x-3 text-slate/40">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
            </div>

            {/* Line Numbers & Content */}
            <div className="flex">
              {/* Line Numbers */}
              <div className="flex-shrink-0 w-12 bg-lightNavy/50 border-r border-accent/10">
                <div className="py-4 text-center">
                  {displaySkills.map((_, index) => (
                    <div key={index} className="h-8 flex items-center justify-center text-xs font-mono text-slate/40">
                      {index + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Code Content */}
              <div className="flex-1 p-6">
                <motion.div 
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
                  layout
                >
                  <AnimatePresence mode="wait">
                    {displaySkills.map((skill, index) => (
                <motion.div
                  key={`${selectedCategory}-${skill.name}`}
                  className="group relative"
                  layout
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, y: -20 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.05,
                    type: "spring",
                    stiffness: 200,
                    damping: 20
                  }}
                  onHoverStart={() => setHoveredSkill(skill.name)}
                  onHoverEnd={() => setHoveredSkill(null)}
                >
                  {/* Skill Card */}
                  <motion.div
                    className="relative p-4 h-24 rounded bg-navy/30 border border-accent/10 hover:border-accent/40 transition-all duration-300 cursor-pointer overflow-hidden backdrop-blur-sm"
                    whileHover={{ 
                      y: -2,
                      backgroundColor: "rgba(15, 23, 42, 0.6)"
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Background Glow */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-br from-accent/5 to-secondary/5 opacity-0 group-hover:opacity-100"
                      transition={{ duration: 0.3 }}
                    />

                    {/* Animated Background Pattern */}
                    <div className="absolute inset-0 opacity-5">
                      {[...Array(3)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent"
                          style={{ top: `${30 + i * 20}%` }}
                          animate={{
                            x: ["-100%", "100%", "-100%"]
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            delay: i * 0.5,
                            ease: "linear"
                          }}
                        />
                      ))}
                    </div>
                    
                    {/* Content Container */}
                    <div className="relative z-10 h-full flex flex-col items-center justify-center">
                      {/* Technology Icon */}
                      <motion.div
                        className="relative mb-2"
                        animate={hoveredSkill === skill.name ? {
                          rotate: [0, -5, 5, -5, 0],
                          scale: [1, 1.1, 1]
                        } : {}}
                        transition={{ 
                          duration: 0.6,
                          ease: "easeInOut"
                        }}
                      >
                        <motion.img
                          src={skill.icon}
                          alt={skill.name}
                          className="w-8 h-8 object-contain mx-auto"
                          style={{
                            filter: hoveredSkill === skill.name 
                              ? 'brightness(1.2) saturate(1.2)' 
                              : 'brightness(0.9) saturate(0.8)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        
                        {/* Fallback */}
                        <div className="hidden w-8 h-8 items-center justify-center bg-accent/20 rounded border border-accent/30 mx-auto">
                          <span className="text-accent font-mono text-xs font-bold">
                            {skill.name.slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      </motion.div>
                      
                      {/* Skill Name */}
                      <motion.h3
                        className="text-xs font-mono font-medium text-center text-lighterSlate group-hover:text-accent transition-colors duration-300"
                        animate={hoveredSkill === skill.name ? { scale: 1.05 } : { scale: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {skill.name}
                      </motion.h3>
                      
                      {/* Category Badge */}
                      <motion.div
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100"
                        initial={{ scale: 0, rotate: -10 }}
                        animate={hoveredSkill === skill.name ? 
                          { scale: 1, rotate: 0, opacity: 1 } : 
                          { scale: 0, rotate: -10, opacity: 0 }
                        }
                        transition={{ duration: 0.3, delay: 0.1 }}
                      >
                        <span className="text-xs font-mono text-accent/80 bg-accent/10 px-1 py-0.5 rounded text-xs">
                          {skill.category}
                        </span>
                      </motion.div>
                    </div>

                    {/* Hover Particles */}
                    {hoveredSkill === skill.name && (
                      <div className="absolute inset-0 pointer-events-none">
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-0.5 h-0.5 bg-accent rounded-full"
                            style={{
                              left: `${30 + i * 20}%`,
                              top: `${20 + i * 20}%`,
                            }}
                            animate={{
                              y: [0, -10, 0],
                              opacity: [0, 1, 0],
                              scale: [0, 1, 0],
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              delay: i * 0.2,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Skills Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="text-center p-6 rounded-lg bg-lightNavy/50 border border-accent/20 hover:border-accent/40 transition-all duration-300"
              whileHover={{ y: -3 }}
            >
              <div className="text-3xl font-semibold text-lighterSlate mb-2 font-mono">
                2+
              </div>
              <div className="text-slate text-sm font-mono">{t('stats.yearsLearning')}</div>
            </motion.div>

            <motion.div
              className="text-center p-6 rounded-lg bg-lightNavy/50 border border-accent/20 hover:border-accent/40 transition-all duration-300"
              whileHover={{ y: -3 }}
            >
              <div className="text-3xl font-semibold text-lighterSlate mb-2 font-mono">
                {allSkills.length}
              </div>
              <div className="text-slate text-sm font-mono">{t('stats.technologies')}</div>
            </motion.div>

            <motion.div
              className="text-center p-6 rounded-lg bg-lightNavy/50 border border-accent/20 hover:border-accent/40 transition-all duration-300"
              whileHover={{ y: -3 }}
            >
              <div className="text-3xl font-semibold text-lighterSlate mb-2 font-mono">
                10+
              </div>
              <div className="text-slate text-sm font-mono">{t('stats.projectsBuilt')}</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InteractiveSkills;